import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SearchResults } from './components/SearchResults';
import { LoginModal } from './components/LoginModal';
import { Footer } from './components/Footer';
import { useAuth } from './contexts/AuthContext';
import { defaultStrings } from './defaultStrings';
import { track, identifyUser, Events } from './analytics';

const config = typeof window === 'undefined' ? { locale: 'en', strings: {} } : (window.__TG_HUNT_CONFIG__ || { locale: 'en', strings: {} });
const strings = { ...defaultStrings, ...(config.strings || {}) };

const styles = {
  page: { minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f8fafc', color: '#172033', fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif' },
  header: { position: 'sticky', top: 0, zIndex: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '12px 18px', background: 'rgba(255,255,255,.94)', borderBottom: '1px solid #e5e7eb', backdropFilter: 'blur(10px)' },
  brand: { display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontWeight: 750 },
  logoSmall: { width: '42px', height: '42px' },
  accountBar: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap', fontSize: '13px' },
  pill: { padding: '7px 10px', borderRadius: '999px', background: '#eef4ff', color: '#174ea6', fontWeight: 650 },
  pillButton: { padding: '7px 10px', border: 0, borderRadius: '999px', background: '#eef4ff', color: '#174ea6', fontWeight: 650, cursor: 'pointer', fontSize: '13px' },
  proPill: { background: '#fff2c7', color: '#7a4b00' },
  accountPopoverWrap: { position: 'relative', display: 'inline-flex' },
  quotaPopover: { position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 40, width: '250px', padding: '14px', borderRadius: '12px', background: '#fff', border: '1px solid #e5e7eb', boxShadow: '0 14px 36px rgba(15,23,42,.18)', color: '#172033', textAlign: 'left' },
  popoverTitle: { marginBottom: '8px', fontWeight: 760 },
  popoverRow: { display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '6px 0', color: '#4b5563' },
  popoverValue: { color: '#172033', fontWeight: 650 },
  button: { padding: '9px 14px', border: 0, borderRadius: '9px', background: '#1a73e8', color: '#fff', fontWeight: 650, cursor: 'pointer' },
  secondaryButton: { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '9px', background: '#fff', color: '#374151', cursor: 'pointer' },
  dangerButton: { padding: '8px 12px', border: '1px solid #fecaca', borderRadius: '9px', background: '#fff5f5', color: '#b42318', cursor: 'pointer' },
  hero: { minHeight: '62vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '54px 18px 80px', textAlign: 'center' },
  logo: { width: '150px', height: '150px' },
  title: { margin: '18px 0 28px', maxWidth: '620px', fontSize: 'clamp(24px,5vw,34px)', lineHeight: 1.25 },
  searchForm: { position: 'relative', width: '100%', maxWidth: '680px' },
  searchInput: { width: '100%', boxSizing: 'border-box', padding: '17px 118px 17px 22px', border: '1px solid #dbe2ea', borderRadius: '999px', background: '#fff', boxShadow: '0 5px 20px rgba(15,23,42,.1)', fontSize: '16px', outline: 'none' },
  searchButton: { position: 'absolute', top: '6px', right: '6px', bottom: '6px', padding: '0 23px', border: 0, borderRadius: '999px', background: '#1a73e8', color: '#fff', fontWeight: 650, cursor: 'pointer' },
  resultsHeader: { display: 'flex', justifyContent: 'center', padding: '28px 16px 20px' },
  notice: { maxWidth: '680px', margin: '12px auto', padding: '12px 16px', borderRadius: '10px', background: '#fff4e5', color: '#7a4510', textAlign: 'center' },
  overlay: { position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '18px', background: 'rgba(15,23,42,.6)' },
  modal: { position: 'relative', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto', padding: '30px', borderRadius: '20px', background: '#fff', boxShadow: '0 24px 80px rgba(0,0,0,.3)' },
  close: { position: 'absolute', right: '14px', top: '12px', border: 0, background: 'transparent', fontSize: '24px', color: '#64748b', cursor: 'pointer' },
  modalTitle: { margin: '0 30px 10px 0', fontSize: '25px' },
  modalLead: { color: '#556174', lineHeight: 1.55 },
  priceCard: { margin: '20px 0', padding: '18px', borderRadius: '14px', background: '#f2f7ff', border: '1px solid #d8e6ff' },
  activeCard: { margin: '20px 0', padding: '18px', borderRadius: '14px', background: '#ecfdf3', border: '1px solid #bbf7d0', color: '#14532d' },
  activeTitle: { marginBottom: '10px', fontSize: '18px', fontWeight: 760 },
  price: { fontSize: '30px', fontWeight: 780, color: '#1557b0', paddingBottom: 15 },
  list: { paddingInlineStart: '22px', color: '#3f4a5a', lineHeight: 1.7 },
  finePrint: { marginTop: '14px', color: '#7b8493', fontSize: '12px', lineHeight: 1.5 },
  status: { margin: '15px 0', padding: '12px', borderRadius: '10px', background: '#ecfdf3', color: '#166534' },
  error: { margin: '15px 0', padding: '12px', borderRadius: '10px', background: '#fef2f2', color: '#b42318' },
  checkoutCta: { width: '100%', padding: '18px 16px', border: 0, borderRadius: '12px', background: '#1a73e8', color: '#fff', fontWeight: 750, fontSize: '18px', cursor: 'pointer' },
  confirmCta: { width: '100%', marginTop: '10px', padding: '8px 12px', border: 0, borderRadius: '8px', background: 'transparent', color: '#64748b', fontWeight: 550, fontSize: '13px', cursor: 'pointer', textDecoration: 'underline' },
};

function authHeaders(token) {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function formatDate(value) {
  if (!value) return '';
  return new Intl.DateTimeFormat(config.locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
}

function formatCountdown(target) {
  if (!target) return '--:--:--';
  const seconds = Math.max(0, Math.ceil((target - Date.now()) / 1000));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remaining = seconds % 60;
  return [hours, minutes, remaining].map((part) => String(part).padStart(2, '0')).join(':');
}

function initialSearchQuery() {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('query') || '';
}

function readCookie(name) {
  if (typeof document === 'undefined') return '';
  const prefix = `${name}=`;
  const cookie = document.cookie.split(';').map((part) => part.trim()).find((part) => part.startsWith(prefix));
  if (!cookie) return '';
  try {
    return decodeURIComponent(cookie.slice(prefix.length));
  } catch {
    return '';
  }
}

const PENDING_ORDER_KEY = 'tghunt_pending_order';

function readPendingOrderId() {
  if (typeof sessionStorage === 'undefined') return '';
  try {
    return sessionStorage.getItem(PENDING_ORDER_KEY) || '';
  } catch {
    return '';
  }
}

function writePendingOrderId(orderId) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    if (orderId) sessionStorage.setItem(PENDING_ORDER_KEY, orderId);
    else sessionStorage.removeItem(PENDING_ORDER_KEY);
  } catch {
    // ignore storage failures
  }
}

function searchBoxEntryId() {
  if (typeof window === 'undefined') return '';
  const fromUrl = new URLSearchParams(window.location.search).get('sb_entry') || '';
  const fromCookie = readCookie('tghunt_search_box_entry');
  const value = (fromUrl || fromCookie).trim().toLowerCase();
  return /^[a-f0-9]{32}$/.test(value) ? value : '';
}

function Paywall({ open, account, busy, error, awaitingConfirm, onClose, onSubscribe, onConfirm }) {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!open) return undefined;
    const timer = setInterval(() => tick((value) => value + 1), 1000);
    return () => clearInterval(timer);
  }, [open]);
  if (!open) return null;

  const subscription = account?.subscription;
  const isPro = account?.tier === 'pro';
  return (
    <div style={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="paywall-title">
      <div style={styles.modal}>
        <button type="button" style={styles.close} onClick={onClose} aria-label={strings.close}>×</button>
        <h2 id="paywall-title" style={styles.modalTitle}>{isPro ? strings.manageProTitle : strings.paywallTitle}</h2>
        <p style={styles.modalLead}>{isPro ? strings.manageProLead : strings.paywallLead}</p>
        {isPro ? (
          <div style={styles.activeCard}>
            <div style={styles.activeTitle}>{strings.proActiveStatus}</div>
            <ul style={{ ...styles.list, color: '#14532d', marginBottom: 0 }}>
              <li>{strings.proQuotaBenefit}</li>
              <li>{strings.proUnlockBenefit}</li>
            </ul>
          </div>
        ) : (
          <div style={styles.priceCard}>
            <div style={styles.price}>{strings.priceAmount || '$2.99'} <span style={{ fontSize: '15px', fontWeight: 500 }}>{strings.every30Days}</span></div>
            <ul style={styles.list}>
              <li>{strings.proQuotaBenefit}</li>
              <li>{strings.proUnlockBenefit}</li>
            </ul>
          </div>
        )}
        {account && <div style={styles.status}>{strings.quotaRemaining}: {account.quota.remaining}/{account.quota.limit} · {strings.resetsIn} {formatCountdown(account.quota.resetAt)}</div>}
        {!isPro && awaitingConfirm && <div style={styles.status}>{strings.confirmPaymentHint}</div>}
        {subscription?.paidThrough && <div style={styles.status}>{strings.accessUntil}: {formatDate(subscription.paidThrough)}</div>}
        {error && <div style={styles.error}>{error}</div>}
        <button type="button" style={styles.checkoutCta} disabled={busy} onClick={onSubscribe}>
          {busy ? strings.working : (isPro ? strings.extendProAccess : strings.emailPaymentLink)}
        </button>
        {!isPro && awaitingConfirm && (
          <button type="button" style={styles.confirmCta} disabled={busy} onClick={onConfirm}>
            {busy ? strings.working : strings.confirmPayment}
          </button>
        )}
        {/* <p style={styles.finePrint}>{isPro ? strings.proBillingFinePrint : strings.billingFinePrint}</p> */}
      </div>
    </div>
  );
}

export function App() {
  const { user, isAuthenticated, isAnonymous, ensureAnonymousUser, logout, loading: authLoading } = useAuth();
  const [query, setQuery] = useState(initialSearchQuery);
  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState({ hasMore: false });
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [loginContext, setLoginContext] = useState('default');
  const [resumePaywallAfterLogin, setResumePaywallAfterLogin] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallTrigger, setPaywallTrigger] = useState('');
  const [billingBusy, setBillingBusy] = useState(false);
  const [billingError, setBillingError] = useState('');
  const [pendingOrderId, setPendingOrderId] = useState(readPendingOrderId);
  const [searchError, setSearchError] = useState('');
  const [showQuotaPopover, setShowQuotaPopover] = useState(false);
  const [, tick] = useState(0);
  const autoSearchStarted = useRef(false);
  const logo = useMemo(() => new URL('assets/logo.png', import.meta.url), []);

  async function authorizedFetch(url, options = {}, fetchUser = user) {
    const token = await fetchUser.getIdToken();
    return fetch(url, { ...options, headers: { ...authHeaders(token), ...(options.headers || {}) } });
  }

  function openPaywall(trigger) {
    setPaywallTrigger(trigger);
    setShowPaywall(true);
  }

  function openDefaultLogin() {
    setLoginContext('default');
    setResumePaywallAfterLogin(false);
    setShowLogin(true);
  }

  function closeLogin() {
    setShowLogin(false);
    setLoginContext('default');
    setResumePaywallAfterLogin(false);
  }

  function promptCheckoutLogin() {
    track(Events.CHECKOUT_LOGIN_REQUIRED, {
      tier: account?.tier || 'unknown',
      trigger: paywallTrigger,
    });
    setBillingError('');
    setResumePaywallAfterLogin(true);
    setLoginContext('checkout');
    setShowPaywall(false);
    setShowLogin(true);
  }

  async function loadAccount(fetchUser = user) {
    if (!fetchUser) return null;
    const response = await authorizedFetch('/api/account', {}, fetchUser);
    if (!response.ok) throw new Error(strings.accountLoadError);
    const data = await response.json();
    setAccount(data);
    return data;
  }

  useEffect(() => {
    if (!user) {
      setAccount(null);
      return;
    }
    loadAccount().catch(() => setSearchError(strings.accountLoadError));
    const queryParams = new URLSearchParams(window.location.search);
    if (queryParams.get('billing')) openPaywall('billing_url');
  }, [user]);

  useEffect(() => {
    identifyUser(user);
  }, [user]);

  useEffect(() => {
    if (!showPaywall) return;
    track(Events.PAYWALL_SHOWN, {
      tier: account?.tier || 'unknown',
      trigger: paywallTrigger,
    });
  }, [showPaywall, account?.tier, paywallTrigger]);

  useEffect(() => {
    const timer = setInterval(() => tick((value) => value + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  // Arrivals from the blog search box land with ?query= already filled in. Run it for
  // them instead of making them press Search a second time for the same query.
  useEffect(() => {
    if (authLoading || autoSearchStarted.current) return;
    const initial = initialSearchQuery().trim();
    if (!initial) return;
    autoSearchStarted.current = true;
    runSearch(initial, 'auto');
  }, [authLoading]);

  useEffect(() => {
    const pending = account?.subscription && ['waiting', 'confirming', 'confirmed', 'sending', 'spending'].includes(account.subscription.status);
    if (!user || !pending) return undefined;
    const timer = setInterval(() => loadAccount().catch(() => {}), 5000);
    return () => clearInterval(timer);
  }, [user, account?.subscription?.status]);

  async function runSearch(rawQuery, source = 'manual') {
    const term = String(rawQuery || '').trim();
    if (!term) return;
    setLoading(true);
    setSearchError('');
    try {
      const searchUser = user || await ensureAnonymousUser();
      if (!searchUser) {
        openDefaultLogin();
        return;
      }
      const params = new URLSearchParams({ query: term, locale: config.locale });
      const entryId = searchBoxEntryId();
      if (entryId) params.set('search_box_entry_id', entryId);
      const response = await authorizedFetch(`/search?${params}`, {}, searchUser);
      const data = await response.json();
      if (data.account) setAccount(data.account);
      if (response.status === 403 && data.code === 'login_required') {
        track(Events.SEARCH_BLOCKED, { reason: 'login_required', source });
        openDefaultLogin();
        return;
      }
      if (response.status === 403 && data.code === 'quota_exceeded') {
        track(Events.SEARCH_BLOCKED, { reason: 'quota_exceeded', source });
        openPaywall('quota_exceeded');
        return;
      }
      if (!response.ok) throw new Error(data.error || strings.searchError);
      setResults(data.results || []);
      setSummary(data.resultSummary || { hasMore: false });
      setSearched(true);
      track(Events.SEARCH_PERFORMED, {
        query_length: term.length,
        result_count: (data.results || []).length,
        status: response.status,
        has_more: data.resultSummary?.hasMore || false,
        source,
      });
    } catch (error) {
      setSearchError(error.message || strings.searchError);
      setResults([]);
      setSearched(true);
    } finally {
      setLoading(false);
    }
  }

  function handleSearch(event) {
    event.preventDefault();
    return runSearch(query, 'manual');
  }

  async function subscribe() {
    const eventProperties = {
      tier: account?.tier || 'unknown',
      trigger: paywallTrigger,
      auth_state: user && !user.isAnonymous && user.email ? 'verified' : 'anonymous',
    };
    track(Events.PAYMENT_BUTTON_CLICKED, { ...eventProperties, outcome: 'checkout_requested' });
    setBillingBusy(true);
    setBillingError('');
    const opener = (window.top && window.top.open) ? window.top : window;
    const paymentWindow = opener.open('about:blank', '_blank');
    try {
      const checkoutUser = user || await ensureAnonymousUser();
      if (!checkoutUser) {
        if (paymentWindow) paymentWindow.close();
        openDefaultLogin();
        return;
      }
      const response = await authorizedFetch('/api/billing/checkout', { method: 'POST' }, checkoutUser);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || strings.billingError);
      track(Events.CHECKOUT_STARTED, {
        ...eventProperties,
        order_id: data.orderId,
      });
      setPendingOrderId(data.orderId);
      writePendingOrderId(data.orderId);
      if (paymentWindow) {
        paymentWindow.location.replace(data.checkoutUrl);
      } else {
        opener.location.assign(data.checkoutUrl);
      }
    } catch (error) {
      if (paymentWindow) paymentWindow.close();
      setBillingError(error.message || strings.billingError);
    } finally {
      setBillingBusy(false);
    }
  }

  async function confirmPayment() {
    setBillingBusy(true);
    setBillingError('');
    try {
      const confirmUser = user || await ensureAnonymousUser();
      if (!confirmUser) {
        openDefaultLogin();
        return;
      }
      const response = await authorizedFetch('/api/billing/confirm', {
        method: 'POST',
        body: JSON.stringify(pendingOrderId ? { orderId: pendingOrderId } : {}),
      }, confirmUser);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || strings.paymentNotFound || strings.billingError);
      if (data.account) setAccount(data.account);
      setPendingOrderId('');
      writePendingOrderId('');
      if (searched && query.trim()) await runSearch(query, 'billing_confirm');
    } catch (error) {
      setBillingError(error.message || strings.paymentNotFound || strings.billingError);
    } finally {
      setBillingBusy(false);
    }
  }

  async function handleLoginSuccess(authenticatedUser) {
    const shouldResumePaywall = resumePaywallAfterLogin;
    await loadAccount(authenticatedUser);
    if (shouldResumePaywall) setShowPaywall(true);
  }

  async function handleLogout() {
    await logout();
    setResults([]);
    setSearched(false);
    setShowPaywall(false);
    setShowLogin(false);
    setLoginContext('default');
    setResumePaywallAfterLogin(false);
    setShowQuotaPopover(false);
  }

  if (authLoading) return <div style={{ ...styles.page, display: 'grid', placeItems: 'center' }}>{strings.loading}</div>;

  return (
    <div style={styles.page} dir={config.locale === 'ar' ? 'rtl' : 'ltr'}>
      <header style={styles.header}>
        <div style={styles.brand} onClick={() => { setSearched(false); setResults([]); }}>
          <img src={logo} alt="TgHunt" style={styles.logoSmall} /> TgHunt
        </div>
        <div style={styles.accountBar}>
          {account && <span style={{ ...styles.pill, ...(account.tier === 'pro' ? styles.proPill : {}) }}>{account.tier === 'pro' ? 'PRO' : strings.freeTier}</span>}
          {account && (
            <span style={styles.accountPopoverWrap}>
              <button
                type="button"
                style={styles.pillButton}
                onClick={() => setShowQuotaPopover((open) => !open)}
                aria-expanded={showQuotaPopover}
              >
                {account.quota.remaining}/{account.quota.limit}
              </button>
              {showQuotaPopover && (
                <div style={styles.quotaPopover} role="status">
                  <div style={styles.popoverTitle}>{strings.quotaDetails}</div>
                  <div style={styles.popoverRow}><span>{strings.quotaTier}</span><span style={styles.popoverValue}>{account.tier === 'pro' ? 'PRO' : strings.freeTier}</span></div>
                  <div style={styles.popoverRow}><span>{strings.quotaRemaining}</span><span style={styles.popoverValue}>{account.quota.remaining}/{account.quota.limit}</span></div>
                  <div style={styles.popoverRow}><span>{strings.resetsIn}</span><span style={styles.popoverValue}>{formatCountdown(account.quota.resetAt)}</span></div>
                  {account.subscription?.paidThrough && (
                    <div style={styles.popoverRow}><span>{strings.accessUntil}</span><span style={styles.popoverValue}>{formatDate(account.subscription.paidThrough)}</span></div>
                  )}
                </div>
              )}
            </span>
          )}
          {account?.tier === 'free' && <button type="button" style={styles.button} onClick={() => openPaywall('upgrade_button')}>{strings.upgrade}</button>}
          {account?.subscription && <button type="button" style={styles.secondaryButton} onClick={() => openPaywall('manage_billing')}>{strings.manageBilling}</button>}
          {isAuthenticated && !isAnonymous ? <button type="button" style={styles.secondaryButton} onClick={handleLogout}>{strings.logout}</button> : <button type="button" style={styles.button} onClick={openDefaultLogin}>{strings.login}</button>}
        </div>
      </header>

      {!searched ? (
        <main style={styles.hero}>
          <img src={logo} alt="TgHunt" style={styles.logo} />
          <h1 style={styles.title}>{strings.appHeading}</h1>
          <form style={styles.searchForm} onSubmit={handleSearch}>
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={strings.searchPlaceholder} style={styles.searchInput} autoFocus />
            <button type="submit" style={styles.searchButton} disabled={loading}>{loading ? strings.loading : strings.searchButton}</button>
          </form>
        </main>
      ) : (
        <main>
          <div style={styles.resultsHeader}>
            <form style={styles.searchForm} onSubmit={handleSearch}>
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={strings.searchPlaceholder} style={styles.searchInput} />
              <button type="submit" style={styles.searchButton} disabled={loading}>{loading ? strings.loading : strings.searchButton}</button>
            </form>
          </div>
          {searchError && <div style={styles.notice}>{searchError}</div>}
          <SearchResults
            results={results}
            isLoading={loading}
            hasMore={summary.hasMore}
            totalResults={summary.total}
            query={query}
            tier={account?.tier || 'unknown'}
            onLocked={() => openPaywall('locked_result')}
            onUpgrade={() => openPaywall('load_more')}
            strings={strings}
          />
        </main>
      )}

      <LoginModal
        isOpen={showLogin}
        context={loginContext}
        strings={strings}
        onClose={closeLogin}
        onSuccess={handleLoginSuccess}
      />
      <Paywall
        open={showPaywall}
        account={account}
        busy={billingBusy}
        error={billingError}
        awaitingConfirm={Boolean(pendingOrderId)}
        onClose={() => setShowPaywall(false)}
        onSubscribe={subscribe}
        onConfirm={confirmPayment}
      />
      <Footer strings={strings} />
    </div>
  );
}
